import { CartLine, ITEM_NOTE_MAX_LENGTH } from '../services/cart.service';
import {
    CartLineModifierView, CartLineView, GetOrder, GetOrderItem, Order, OrderForm, OrderItem, OrderSubmissionOptions,
    PaymentMethod, PickupFormField, RejectedItem, SelectedModifier,
} from '../models/cart.model';
import { Item, Modifier, Recommendations, Section } from '../models/menu.model';
import { getSearchGroups } from '../menu/menu.utils';
import { ItemStatus } from '../shared/enums';
import { PayNowAtTableRequest } from '../models/pay-at-table.model';

export function cartSubtotal(lines: CartLineView[]): number {
    return lines.reduce((sum, line) => sum + line.total, 0);
}

// The pickup form becomes mandatory once the subtotal crosses the restaurant-configured
// threshold, or always, when a form is configured without one.
export function isOrderFormRequired(form: OrderForm | undefined, subtotal: number): boolean {
    if (!form) return false;

    const threshold = form.metadata?.['min_order'];
    return threshold === undefined ? true : subtotal > threshold;
}

// Venues may only configure the form in some languages (e.g. 'en' only),
// so fall back to English, then to whichever language exists.
export function pickupFieldsFor(form: OrderForm, lang: string): PickupFormField[] {
    return form.content[lang]
        ?? form.content['en']
        ?? Object.values(form.content)[0]
        ?? [];
}

export function toOrderItems(lines: CartLine[]): OrderItem[] {
    return lines.map(line => ({
        item: line.item.id,
        comment: (line.comment || '').slice(0, ITEM_NOTE_MAX_LENGTH).trim(),
        quantity: line.quantity,
        modifiers: line.modifiers,
    }));
}

export function toCartLineView(line: CartLine): CartLineView {
    const modifiers = line.modifiers.map(selected => toModifierView(line.item, selected));
    const modifiersTotal = modifiers.reduce((sum, mod) => sum + mod.price * mod.quantity, 0);

    return {
        id: line.id,
        itemName: line.item.name,
        quantity: line.quantity,
        modifiers,
        comment: line.comment,
        total: (line.item.price + modifiersTotal) * line.quantity,
        subSectionId: line.subSectionId,
    };
}

export function toCartLineViewFromOrderItem(item: GetOrderItem, index: number): CartLineView {
    return {
        id: `${item.item}-${index}`,
        itemName: item.item,
        quantity: item.quantity,
        modifiers: item.modifiers.map(modifier => ({
            name: modifier.modifier,
            price: modifier.unit_price,
            quantity: modifier.quantity,
        })),
        comment: item.comment,
        total: item.total_price_without_discount ?? item.total_price,
        pickedUp: item.picked_up,
    };
}

function toModifierView(item: Item, selected: SelectedModifier): CartLineModifierView {
    const modifier = findModifier(item, selected.id);
    return {
        name: modifier?.name ?? '',
        price: modifier?.price ?? 0,
        quantity: selected.quantity,
    };
}

function findModifier(item: Item, modifierId: string): Modifier | undefined {
    return item.modifier_groups
        .flatMap(group => group.modifiers)
        .find(modifier => modifier.id === modifierId);
}

export interface CreateOrderParams extends OrderSubmissionOptions {
    lines: CartLine[];
    userId: string;
    comment: string;
    tipAmount: number;
    voucher: string | null;
    isPayAtTable: boolean;
    metadata?: Record<string, unknown>;
    paymentType?: string;
}

export function createOrder(params: CreateOrderParams): Order {
    const order: Order = {
        order_items: toOrderItems(params.lines),
        user_id: params.userId,
        comment: params.comment,
        metadata: params.metadata,
        voucher_name: (params.voucherName ?? params.voucher) || '',
        tip_amount: params.tipAmount,
        payment_type: params.paymentType,
    };

    // pay_now only means anything for a Pay at Table order — a normal "place order"/
    // "add to bill" submission from the same button omits it entirely.
    if (params.isPayAtTable) {
        order.pay_now = params.payNow ?? false;
    }

    return order;
}

export function toPayNowAtTableRequest(order: GetOrder): PayNowAtTableRequest {
    return {
        cheque_id: order.cheque?.id ?? '',
        total_amount: Math.round((order.total_price_without_discount || 0) * 100) / 100,
    };
}

export interface PaymentMethodAvailability {
    ezetapMachineId: string | null;
    applePayAvailable: boolean;
    googlePayAvailable: boolean;
}

// An active ezetap terminal takes over as the only payment method; otherwise the
// wallet methods are offered only when the current device/browser actually supports them.
export function filterAvailablePaymentMethods(methods: PaymentMethod[], availability: PaymentMethodAvailability): PaymentMethod[] {
    if (availability.ezetapMachineId) {
        return methods.filter(method => method.key === 'ezetap');
    }

    return methods
        .filter(method => method.key !== 'ezetap')
        .filter(method => availability.applePayAvailable || method.key !== 'applepay')
        .filter(method => availability.googlePayAvailable || method.key !== 'googlepay');
}

export function idsForRejectedItems(lines: CartLine[], items: RejectedItem[]): string[] {
    const ids = new Set(items.map(item => item.id));
    return lines.filter(line => ids.has(line.item.id)).map(line => line.id);
}

export function idsForRejectedModifiers(lines: CartLine[], modifiers: RejectedItem[]): string[] {
    const modifierIds = new Set(modifiers.map(modifier => modifier.id));
    return lines.filter(line => line.modifiers.some(modifier => modifierIds.has(modifier.id))).map(line => line.id);
}

export interface RecommendedItem {
    item: Item;
    subSectionId?: string;
    isOptionalSubSection?: boolean;
}

// Candidate ids in display order - curated, then auto (highest confidence first), then
// popular - each id once, at its first position, and none already in the cart. Not capped:
// max_items is applied after resolving against the menu (resolveRecommendations), so an id
// that can't be shown lets the next one take its place.
export function rankRecommendationIds(recommendations: Recommendations, cartItemIds: ReadonlySet<string>): string[] {
    // An item recommended by several cart items ranks by its strongest link.
    const autoConfidence = new Map<string, number>();
    for (const cartItemId of cartItemIds) {
        for (const { id, confidence } of recommendations.auto?.[cartItemId] ?? []) {
            autoConfidence.set(id, Math.max(confidence, autoConfidence.get(id) ?? -Infinity));
        }
    }
    const auto = [...autoConfidence].sort(([, a], [, b]) => b - a).map(([id]) => id);

    const ranked = new Set<string>();
    for (const id of [...(recommendations.curated ?? []), ...auto, ...(recommendations.popular ?? [])]) {
        if (!cartItemIds.has(id)) ranked.add(id);
    }
    return [...ranked];
}

// Where each recommendable item gets added from: its first orderable placement on the menu,
// so its cart line (and the card's quantity) matches adding it from that section. Combo
// groups are skipped - adding a combo slot outside the combo flow would confuse the guide -
// so an item that only exists inside combos isn't recommendable at all.
export function recommendablePlacements(sections: Section[], comboGroupIds: ReadonlySet<string>): Map<string, RecommendedItem> {
    const placements = new Map<string, RecommendedItem>();
    for (const group of getSearchGroups(sections)) {
        if (comboGroupIds.has(group.superSectionId)) continue;

        for (const item of group.items) {
            if (item.status !== ItemStatus.Enabled || placements.has(item.id)) continue;
            placements.set(item.id, { item, subSectionId: group.subSectionId, isOptionalSubSection: group.isOptionalSubSection });
        }
    }
    return placements;
}

// Used when the backend doesn't set max_items. An explicit 0 still hides the block.
export const DEFAULT_MAX_RECOMMENDATIONS = 4;

export function resolveRecommendations(
    ids: string[], placements: Map<string, RecommendedItem>, maxItems: number | null | undefined,
): RecommendedItem[] {
    const limit = maxItems ?? DEFAULT_MAX_RECOMMENDATIONS;
    return ids
        .map(id => placements.get(id))
        .filter((placement): placement is RecommendedItem => !!placement)
        .slice(0, Math.max(limit, 0));
}
